<template>
  <div class="flex flex-col gap-2">
    <!-- A segmented row, not a switch. A switch has two positions and there are
         three states here, the default being the one that is neither — "follow
         the phone" cannot be represented as on or off. -->
    <div
      aria-label="Appearance"
      class="relative flex w-full rounded-full bg-surface p-1"
      role="radiogroup"
    >
      <!-- The selection is one object that moves, rather than three fills that
           blink. It is also the only thing on the row with a background, which
           is what lets the row itself carry no border at all.
           aria-hidden: the buttons already say which is chosen.

           The track is repeated as an overlay so the thumb can be w-1/3 of the
           same padded box the buttons divide. A width worked out against the
           outer box needs a calc that restates the padding, and then the two
           disagree the moment the padding changes. -->
      <span
        aria-hidden="true"
        class="pointer-events-none absolute inset-0 flex p-1"
      >
        <span
          class="w-1/3 rounded-full bg-segment shadow-chip transition-transform duration-base ease-move motion-reduce:transition-none"
          :style="{ transform: `translateX(${activeIndex * 100}%)` }"
        />
      </span>

      <button
        v-for="option in OPTIONS"
        :key="option.value"
        :aria-checked="theme.choice === option.value"
        class="min-h-btn-xs relative flex-1 rounded-full px-3 text-action pj-tap"
        :class="theme.choice === option.value ? 'text-ink' : 'text-muted hover:text-ink'"
        role="radio"
        type="button"
        @click="theme.set(option.value)"
      >
        {{ option.label }}
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { PjThemeChoice } from '@/domains/pjazza/store/use-theme-store/useThemeStore.ts'

import { usePjazzaThemeStore } from '@/domains/pjazza/store/use-theme-store/useThemeStore.ts'

/**
 * Light, dark, or whatever the phone is doing.
 *
 * One component used on the account screen and inside the venue menu sheet. A
 * diner spends the whole meal on a venue page, and that context has no tab bar to
 * reach settings through — so the control has to exist there, and a second copy
 * would be a second thing to keep in step.
 *
 * `System` is first and named plainly rather than shown as a magic-wand icon. It
 * is the default, and a diner who has never thought about this should be able to
 * see that the app is following their phone rather than guessing.
 *
 * Neutral throughout. The selected segment used to be a primary-container fill,
 * which spent the screen's one filled brand object on a setting — and on the
 * account screen that put a blue slab directly above the buttons that actually
 * do something. A raised pure surface on a tinted rail says "chosen" without
 * spending anything.
 */
const theme = usePjazzaThemeStore()

const OPTIONS: { value: PjThemeChoice; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' }
]

const activeIndex = computed(() => OPTIONS.findIndex(option => option.value === theme.choice))
</script>
